import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'

import { computed, ref, watch } from 'vue'

import { useAdminSidebarMenuLinks } from '@/features/layout/components/constants/useAdminSidebarMenuLinks.js'
import { useBreakpoint } from '@/features/ui/hooks/useBreakpoint.js'

/**
 * @typedef {Object} BreadcrumbItem
 * @property {string} label
 * @property {string} to
 */

const defaultLogoSrc = '/images/admin/footer-logo.png'
const helloClubLogoSrc = '/images/admin/footer-logo-hello-club.png'

const themeLocalStorageKey = 'fmAdminTheme'
const lightDarkThemeKey = 'appearance'

export const useLayoutStore = defineStore('layoutStore', function () {
  const { isTablet } = useBreakpoint()
  const sidebarOpened = useLocalStorage('adminSidebarOpened', false)
  const breadcrumbs = ref([])
  const loadingOperations = ref(new Map())
  const pageTitle = ref('')
  const hamburgerTitle = ref('')
  const hamburgerLeftButton = ref(null)
  const headerLeftActions = ref([])
  const headerRightActions = ref([])

  const sidebarMenuItems = ref([])

  const showBackButton = ref(false)
  const backButtonListener = ref(null)

  const footerLogoSrc = ref(defaultLogoSrc)

  const colorScheme = useLocalStorage(themeLocalStorageKey, 'default')
  const appearance = useLocalStorage(lightDarkThemeKey, 'fm-light')

  function setTheme(schemeName) {
    const root = document.querySelector(':root')
    root.classList.remove('theme-default', 'theme-helloclub')
    root.classList.add(`theme-${schemeName}`)
    colorScheme.value = schemeName

    if (schemeName === 'helloclub') {
      setFooterLogoSrc(helloClubLogoSrc)
    }

    if (schemeName === 'default') {
      setFooterLogoSrc(defaultLogoSrc)
    }
  }

  setTheme(colorScheme.value)

  watch(
    appearance,
    value => {
      const root = document.querySelector(':root')
      root.classList.remove('fm-light', 'fm-dark')
      root.classList.add(value)
    },
    {
      immediate: true,
    },
  )

  function setAppearance(value) {
    appearance.value = value
  }

  function toggleAppearance() {
    setAppearance(appearance.value === 'fm-light' ? 'fm-dark' : 'fm-light')
  }

  const isDarkMode = computed(() => appearance.value === 'fm-dark')

  watch(isTablet, value => {
    sidebarOpened.value = !value
  })

  function toggleSidebar() {
    sidebarOpened.value = !sidebarOpened.value
  }

  function closeSidebar() {
    sidebarOpened.value = false
  }

  function openSidebar() {
    sidebarOpened.value = true
  }

  function setBreadcrumbs(_breadcrumbs) {
    breadcrumbs.value = _breadcrumbs
  }

  function startLoading(operationName) {
    loadingOperations.value.set(operationName, true)
  }

  function finishLoading(operationName) {
    loadingOperations.value.delete(operationName)
  }

  function isLoadingOperation(operationName) {
    return loadingOperations.value.has(operationName)
  }

  function setPageTitle(title) {
    pageTitle.value = title
    window.document.title = title
  }

  function setHamburgerTitle(title) {
    hamburgerTitle.value = title
  }

  function setHeaderLeftActions(actions) {
    headerLeftActions.value = actions.filter(Boolean)
  }

  function resetHeaderLeftActions() {
    headerLeftActions.value = []
  }

  function setHeaderRightActions(actions) {
    headerRightActions.value = actions.filter(Boolean)
  }

  function resetHeaderRightActions() {
    headerRightActions.value = []
  }

  function setSidebarMenuItems(items) {
    sidebarMenuItems.value = items
  }

  function resetSidebarMenuItems() {
    sidebarMenuItems.value = useAdminSidebarMenuLinks()
  }

  function setHamburgerLeftButton(button) {
    hamburgerLeftButton.value = button
  }

  function setBackButtonVisibility(value) {
    showBackButton.value = value
  }

  function setBackButtonListener(listener) {
    backButtonListener.value = listener
  }

  function setFooterLogoSrc(src) {
    footerLogoSrc.value = src
  }

  return {
    sidebarOpened,
    toggleSidebar,
    closeSidebar,
    openSidebar,

    breadcrumbs,
    setBreadcrumbs,

    loadingOperations,
    startLoading,
    finishLoading,
    isLoadingOperation,

    setPageTitle,
    pageTitle,

    setHamburgerTitle,
    hamburgerTitle,

    setHeaderLeftActions,
    resetHeaderLeftActions,
    headerLeftActions,

    setHeaderRightActions,
    resetHeaderRightActions,
    headerRightActions,

    resetSidebarMenuItems,
    setSidebarMenuItems,
    sidebarMenuItems,

    setHamburgerLeftButton,
    hamburgerLeftButton,

    setBackButtonVisibility,
    showBackButton,
    setBackButtonListener,
    backButtonListener,

    setFooterLogoSrc,
    footerLogoSrc,

    setTheme,

    setAppearance,
    appearance,
    toggleAppearance,
    isDarkMode,
  }
})
